import React from 'react';
import {
  Form,
  Input,
  DatePicker,
  TimePicker,
  InputNumber,
  Button,
  message,
  Space,
  Divider,
  Typography,
  Row,
  Col,
  Select
} from 'antd';
import { FileTextOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useDocumentGeneration from '../../hooks/useDocumentGeneration';
import { colors, spacing, radius, typography } from '../../styles/monochromeTheme';

const { Text } = Typography;

// Monochrome styles
const styles = {
  page: {
    minHeight: '100vh',
    background: colors.bgPrimary,
    padding: spacing.xxl,
  },
  card: {
    background: colors.bgSecondary,
    borderRadius: radius.xxxl,
    border: `1px solid ${colors.borderLight}`,
    padding: spacing.xxl,
  },
  presetBtn: {
    fontSize: '11px',
    height: '32px',
    borderRadius: radius.md,
    background: colors.bgTertiary,
    border: `1px solid ${colors.borderMedium}`,
    color: colors.textSecondary,
  },
  primaryBtn: {
    height: '52px',
    borderRadius: radius.xl,
    background: colors.bgDark,
    border: 'none',
    color: colors.textInverse,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
  },
  infoBox: {
    padding: spacing.lg,
    backgroundColor: colors.bgTertiary,
    borderRadius: radius.lg,
    border: `1px solid ${colors.borderSubtle}`,
  },
};

function SeabobDocumentGenerator() {
  const [form] = Form.useForm();
  const { generateDoc, loading } = useDocumentGeneration();

  const TIME_PRESETS = [
    { label: 'Full Day', departure: '09:00', returning: '17:00' },
    { label: 'Half Day Matin', departure: '09:00', returning: '13:00' },
    { label: 'Half Day AM', departure: '13:00', returning: '17:00' },
    { label: '2h Matin', departure: '09:00', returning: '11:00' },
    { label: '2h AM', departure: '15:00', returning: '17:00' },
  ];

  const applyTimePreset = (departure, returning) => {
    form.setFieldsValue({
      departure_hour: dayjs(departure, 'HH:mm'),
      returning_hour: dayjs(returning, 'HH:mm')
    });
  };

  const calculateTotal = () => {
    const seabobPrice = form.getFieldValue('seabob_price') || 0;
    const esurfPrice = form.getFieldValue('esurf_price') || 0;
    const additionalPrice = form.getFieldValue('additional_price') || 0;
    const total = seabobPrice + esurfPrice + additionalPrice;
    form.setFieldsValue({ total_price: total });
  };

  const handleGenerateContrat = async (values) => {
    try {
      const charterId = `SEABOB-${dayjs().format('YYYYMMDD-HHmmss')}`;

      const documentData = {
        charterer_date: values.charterer_date ? dayjs(values.charterer_date).format('DD/MM/YYYY') : '',
        charter_date: values.charterer_date ? dayjs(values.charterer_date).format('DD/MM/YYYY') : '',
        departure_hour: values.departure_hour ? values.departure_hour.format('HH:mm') : '',
        returning_hour: values.returning_hour ? values.returning_hour.format('HH:mm') : '',
        client_name: values.client_name || '',
        spot: values.spot || 'SBH',
        seabob_number: values.seabob_number || '',
        seabob_duration: values.seabob_duration || '',
        seabob_price: values.seabob_price ? `${values.seabob_price}€` : '',
        esurf_number: values.esurf_number || '',
        esurf_duration: values.esurf_duration || '',
        esurf_price: values.esurf_price ? `${values.esurf_price}€` : '',
        additional_row: values.additional_row || '',
        addtional_price: values.additional_price ? `${values.additional_price}€` : '',
        total_price: values.total_price ? `${values.total_price}€` : '',
      };

      await generateDoc('Contrat', documentData, charterId, 'seabob');
      form.resetFields();
    } catch (error) {
      console.error('Error:', error);
      let errorMessage = 'Erreur lors de la génération du contrat';
      if (error && error.message) errorMessage += ': ' + error.message;
      message.error({ content: errorMessage, duration: 10 });
    }
  };

  return (
    <div style={styles.page}>
      <style>
        {`
          .ant-form-item-label > label { font-size: 11px !important; font-weight: 600 !important; color: ${colors.textSecondary} !important; text-transform: uppercase; letter-spacing: 0.5px; }
          .ant-input, .ant-select-selector, .ant-picker, .ant-input-number { border-radius: ${radius.md} !important; border-color: ${colors.borderMedium} !important; }
          .ant-input:focus, .ant-select-selector:focus, .ant-picker-focused { border-color: ${colors.bgDark} !important; box-shadow: 0 0 0 2px rgba(0,0,0,0.1) !important; }
          .ant-divider-inner-text { font-size: 11px !important; font-weight: 600 !important; color: ${colors.textMuted} !important; text-transform: uppercase; letter-spacing: 1px; }
        `}
      </style>

      <div style={styles.card}>
        <Divider style={{ marginTop: 0 }}>Contract Information</Divider>

        <Form form={form} layout="vertical" onFinish={handleGenerateContrat} initialValues={{ spot: 'SBH' }}>
          <Row gutter={[16, 0]}>
            <Col span={8}>
              <Form.Item name="charterer_date" label="Date" rules={[{ required: true, message: 'Requis' }]}>
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%', height: '44px' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="departure_hour" label="Departure Time" rules={[{ required: true, message: 'Requis' }]}>
                <TimePicker format="HH:mm" style={{ width: '100%', height: '44px' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="returning_hour" label="Return Time" rules={[{ required: true, message: 'Requis' }]}>
                <TimePicker format="HH:mm" style={{ width: '100%', height: '44px' }} />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginBottom: '20px' }}>
            <Text type="secondary" style={{ fontSize: '11px', marginBottom: '8px', display: 'block', textTransform: 'uppercase', letterSpacing: '1px' }}>
              <ClockCircleOutlined /> Quick times
            </Text>
            <Space wrap size="small">
              {TIME_PRESETS.map((preset) => (
                <Button key={preset.label} size="small" onClick={() => applyTimePreset(preset.departure, preset.returning)} style={styles.presetBtn}>
                  {preset.label} ({preset.departure}-{preset.returning})
                </Button>
              ))}
            </Space>
          </div>

          <Row gutter={[16, 0]}>
            <Col span={18}>
              <Form.Item name="client_name" label="Client Name" rules={[{ required: true, message: 'Requis' }]}>
                <Input style={{ height: '44px' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="spot" label="Spot" initialValue="SBH" rules={[{ required: true, message: 'Requis' }]}>
                <Select style={{ height: '44px' }}>
                  <Select.Option value="SBH">SBH</Select.Option>
                  <Select.Option value="AXA">AXA</Select.Option>
                  <Select.Option value="SXM">SXM</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '16px 0' }}>Seabob & E-Surf</Divider>

          <Row gutter={[16, 0]}>
            <Col span={8}>
              <Form.Item name="seabob_number" label="Nb Seabob">
                <Input placeholder="2" style={{ height: '44px' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="seabob_duration" label="Duration">
                <Input placeholder="2 heures" style={{ height: '44px' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="seabob_price" label="Price (€)">
                <InputNumber style={{ width: '100%', height: '44px' }} min={0} onChange={calculateTotal} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={8}>
              <Form.Item name="esurf_number" label="Nb E-Surf">
                <Input placeholder="1" style={{ height: '44px' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="esurf_duration" label="Duration">
                <Input placeholder="1 heure" style={{ height: '44px' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="esurf_price" label="Price (€)">
                <InputNumber style={{ width: '100%', height: '44px' }} min={0} onChange={calculateTotal} />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '16px 0' }}>Total</Divider>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item name="additional_row" label="Additional (optional)">
                <Input placeholder="Extra service" style={{ height: '44px' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="additional_price" label="Extra Price (€)">
                <InputNumber style={{ width: '100%', height: '44px' }} min={0} onChange={calculateTotal} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="total_price" label="Total (€)" rules={[{ required: true, message: 'Requis' }]}>
                <InputNumber style={{ width: '100%', height: '44px', fontWeight: 700 }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ textAlign: 'center', marginTop: spacing.xl }}>
            <Button type="primary" htmlType="submit" icon={<FileTextOutlined />} size="large" loading={loading} style={styles.primaryBtn}>
              Generate Contract
            </Button>
          </div>
        </Form>

        <Divider style={{ margin: `${spacing.xl} 0` }} />

        <div style={styles.infoBox}>
          <Text strong>Workflow:</Text>
          <div style={{ marginTop: spacing.sm, lineHeight: '1.6', color: colors.textTertiary, fontSize: typography.fontSize.sm }}>
            After generation, the contract appears in <strong>Document List</strong>.
            You can then generate Confirmation and Invoice from that page.
          </div>
        </div>
      </div>
    </div>
  );
}

export default SeabobDocumentGenerator;
